/** @file Yaw and pitch (degrees) read back from a rotation: pitch from forward/up, yaw roll-free (21.1). */
import type { Quat } from './quat';
import { quatRotateVec3 } from './quatRotateVec3';

const DEG = 180 / Math.PI;
/** |f.z| and |u.z| below this both count as 0 (the gimbal case of 21.1: atan2(0, 0) = 0), so float noise cannot pick a pitch. */
const GIMBAL = 1e-12;

/**
 * `[yawDeg, pitchDeg]` per design 21.1: forward `f = q (0, -1, 0)`, up `u = q (0, 0, 1)`; `pitch = atan2(f.z, u.z)` in
 * (-180, 180]; the roll-free forward `h = f cos(pitch) - u sin(pitch)` (h.z = 0) gives `yaw = atan2(-h.x, -h.y)` in
 * [0, 360). Pitch covers the full circle (a node tipped past vertical reads |pitch| > 90 with the yaw of its roll-free
 * frame; folding is the lookup's job). Gimbal (`f.z = u.z = 0`): pitch 0 and h = f. Not the heading of f for rolled,
 * steep nodes.
 */
export function yawPitchOf(q: Quat): [number, number] {
  const [fx, fy, fz] = quatRotateVec3(q, [0, -1, 0]);
  const [ux, uy, uz] = quatRotateVec3(q, [0, 0, 1]);
  const p = Math.abs(fz) < GIMBAL && Math.abs(uz) < GIMBAL ? 0 : Math.atan2(fz, uz);
  const c = Math.cos(p);
  const s = Math.sin(p);
  const hx = fx * c - ux * s;
  const hy = fy * c - uy * s;
  // Heading of yaw y is (-sin y, -cos y): clockwise from south seen from above.
  const yaw = ((Math.atan2(-hx, -hy) * DEG) % 360 + 360) % 360;
  const pitch = p * DEG;
  return [yaw >= 360 ? 0 : yaw + 0, pitch <= -180 ? 180 : pitch + 0];
}
